import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import { ascend, descend, isEmpty, prop, propOr, sortWith } from 'ramda';
import { isNilOrEmpty, noop } from 'ramda-extension';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';
import Eye from '@gef-ui/material-icons/Eye';

import { ComponentLoader, TaskFeedback } from 'components';
import fetch from 'features/common/fetch';
import typeTasks from 'features/other/typeTasks';
import taskDetail from 'features/views/taskDetail';
import { closeFeedback as resetTemporarilySetFeedbackById } from 'features/views/taskFeedback/actions';
import taskOverview from 'features/views/taskOverview';
import { equalOrThreeDashes, fromIsoDate, getCodelistPropById, getRoutePath } from 'utils';
import { emptyString, joinString } from 'utils/string';

export const TaskTableBody = ({
	isDetailVisible,
	onDetailVisibilityChange,
	task = {},
	productsCodelist,
	taskSubtypesCodelist,
	hasTrafficLights,
	isTask,
	isSelected,
	isNotProcessedTask,
	markSelectedAllState,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const handleRedirectToDetail = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			dispatch(resetTemporarilySetFeedbackById(task?.taskTypeId));
			dispatch(typeTasks.actions.setPersistTypeTasksVisibleState(true));

			const { pathname, state } = getRoutePath(
				'detail',
				{
					taskId: task.id,
				},
				true,
			);

			state.read = task.read;
			navigate(pathname, { state });
		},
		[task, dispatch, navigate],
	);

	const handleUnviewTask = useCallback(
		(taskId) => (event) => {
			event.preventDefault();
			event.stopPropagation();

			dispatch(taskDetail.actions.unsetTaskViewed(taskId)).then(() => {
				dispatch(typeTasks.actions.setTaskView(task.id, false));
				isDetailVisible && onDetailVisibilityChange();
			});
		},
		[dispatch, task, isDetailVisible, onDetailVisibilityChange],
	);

	const handleSelectTask = useCallback(
		(taskId) => () => {
			dispatch(typeTasks.actions.selectTask(task.taskTypeId, taskId));
		},
		[dispatch, task],
	);

	const handleToggleTaskFeedback = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();

			onDetailVisibilityChange();
			dispatch(resetTemporarilySetFeedbackById(task?.taskTypeId));
		},
		[onDetailVisibilityChange, dispatch, task?.taskTypeId],
	);

	const handleSuccessFeedback = useCallback(() => {
		dispatch(typeTasks.actions.reloadCurrentPage(task.taskTypeId));
		dispatch(taskOverview.actions.fetchTaskTypeList('all'));
	}, [dispatch, task]);

	const shortDescription = getCodelistPropById(task.taskSubtypeId, taskSubtypesCodelist, 'shortDescription');
	const { client = {}, policy = {} } = task;
	const { address = {} } = client;
	const { policyAdmins = [], productLines = [] } = policy;

	const sortByOrderNrAndSellerIn = sortWith([ascend(prop('orderNr')), descend(propOr(0, 'sellerIn'))]);

	const generatePolicyAdmins = (policyAdmins) => {
		const addNumOrder = policyAdmins.length > 1;

		const formattedAdmins = policyAdmins.map((policyAdmin) => {
			// does admin have some name
			const isAdminActive = !!policyAdmin.firstName || !!policyAdmin.surname;

			return {
				...policyAdmin,
				isAdminActive,
			};
		});

		const sortedFormattedAdmins = sortByOrderNrAndSellerIn(formattedAdmins);

		if (addNumOrder) {
			return sortedFormattedAdmins.map(({ orderNr, firstName, surname, isAdminActive, sellerIn }, i) => {
				const nameToUse = isAdminActive
					? joinString(' ', [surname, firstName])
					: getLabelMessage('tasksOverview:inactivePolicyAdmin');

				return (
					<Fragment key={i}>
						{!isNilOrEmpty(sellerIn) ? (
							<div>{joinString('. ', [orderNr, nameToUse])}</div>
						) : (
							<div className="Subseller-bulletpoint">{nameToUse}</div>
						)}
					</Fragment>
				);
			});
		} else {
			return sortedFormattedAdmins.map(({ firstName, surname, isAdminActive }, i) => {
				const nameToUse = isAdminActive
					? joinString(' ', [surname, firstName])
					: getLabelMessage('tasksOverview:inactivePolicyAdmin');

				return (
					<Fragment key={i}>
						{nameToUse}
						<br />
					</Fragment>
				);
			});
		}
	};

	const products = productLines.map((productId) =>
		getCodelistPropById(productId, productsCodelist, 'shortDescription'),
	);

	const clientName = client?.businessName
		? client.businessName
		: joinString(' ', [client?.surname, client?.firstName]);

	const trafficLightColor = useMemo(() => {
		const systemTime = format(new Date(), 'yyyy-MM-dd');

		if (systemTime <= task?.notificationDate) {
			return 'success';
		} else if (task?.notificationDate < systemTime && systemTime <= task?.deadlineDate) {
			return 'warning';
		} else {
			return 'error';
		}
	}, [task?.deadlineDate, task?.notificationDate]);

	const showTrafficLightIndicator = hasTrafficLights && !task?.processed;

	return (
		<>
			<TableRow
				sx={{
					borderBottom: 'none',
					backgroundColor: markSelectedAllState
						? !isSelected
							? 'primary.shades.8p'
							: 'initial'
						: isSelected
						  ? 'primary.shades.8p'
						  : 'initial',
					'& td': {
						fontWeight: !task.read ? 'bold' : 'normal',
						borderBottom: 'none',
					},
				}}
				onClick={!task.processed ? handleSelectTask(task.id) : noop}
			>
				<TableCell
					sx={{
						borderLeft: showTrafficLightIndicator
							? (theme) => `2px solid ${theme.palette[trafficLightColor].main}`
							: 'none',
					}}
				>
					<div>{equalOrThreeDashes(task?.id)}</div>
					<div>{equalOrThreeDashes(shortDescription)}</div>
				</TableCell>
				<TableCell>{equalOrThreeDashes(clientName)}</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(fromIsoDate(task.dateCreated))}</div>
					<div>{equalOrThreeDashes(fromIsoDate(task.deadlineDate))}</div>
				</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(policy.policyNo)}</div>
					<div>
						{!isEmpty(products)
							? products.map((product, i) => <div key={i}>{product}</div>)
							: emptyString()}
					</div>
				</TableCell>
				<TableCell>
					<div>{equalOrThreeDashes(joinString(' ', [address?.street, address?.streetNo]))}</div>
					<div>{equalOrThreeDashes(joinString(' ', [address?.zipCode, address?.municipality]))}</div>
				</TableCell>
				<TableCell>{isNilOrEmpty(policyAdmins) ? emptyString() : generatePolicyAdmins(policyAdmins)}</TableCell>
				<TableCell>
					<Grid
						container
						columnSpacing={1}
						sx={{
							alignItems: 'center',
							justifyContent: 'flex-end',
						}}
					>
						<Grid size={4}>
							{isTask && isNotProcessedTask && task?.read && !task?.processed && (
								<ComponentLoader fetchStorePath={fetch.constants.urls.taskUnread} ignoreErrors>
									<IconButton
										sx={{ color: 'primary.main' }}
										disableRipple
										onClick={handleUnviewTask(task.id)}
									>
										<Eye />
									</IconButton>
								</ComponentLoader>
							)}
						</Grid>
						<Grid size={4}>
							{task?.id && (
								<ComponentLoader
									fetchStorePath={`${fetch.constants.urls.taskOpen}.${task.id}`}
									ignoreErrors
								>
									<ComponentLoader
										fetchStorePath={`${fetch.constants.urls.taskUnread}.${task.id}`}
										ignoreErrors
									>
										<Link
											underline="none"
											onClick={handleRedirectToDetail}
											sx={{
												fontWeight: 'bold',
											}}
										>
											{getLabelMessage('common:detail')}
										</Link>
									</ComponentLoader>
								</ComponentLoader>
							)}
						</Grid>
						<Grid size={4}>
							{isTask && isNotProcessedTask && (
								<Box onClick={task.read && !task.processed ? handleToggleTaskFeedback : noop}>
									{isDetailVisible ? (
										<ChevronUp
											sx={{
												color: 'primary.main',
												opacity: !task.read || task.processed ? 0.26 : 1,
												cursor: !task.read || task.processed ? 'no-drop' : 'pointer',
											}}
										/>
									) : (
										<ChevronDown
											sx={{
												color: 'primary.main',
												opacity: !task.read || task.processed ? 0.26 : 1,
												cursor: !task.read || task.processed ? 'no-drop' : 'pointer',
											}}
										/>
									)}
								</Box>
							)}
						</Grid>
					</Grid>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ py: 0 }} colSpan={9}>
					<Collapse in={isDetailVisible && isNotProcessedTask} unmountOnExit>
						<Box sx={{ mt: 1, mb: 2 }}>
							{/* minHeight hack - when taskFeedback is not rendered, we have stack placeholder with minHeight 
							so the easing animation is still smooth, collapse has something to "hide" */}
							<Stack
								id="taskFeedback"
								sx={{
									minHeight: '35px',
								}}
							>
								{/* conditional rendering - when closing/opening TaskFeedback Form is rendered or wiped 
								we cannot have same form multiple times at the same time */}
								{isDetailVisible && <TaskFeedback task={task} onSuccess={handleSuccessFeedback} />}
							</Stack>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

TaskTableBody.propTypes = {
	hasTrafficLights: PropTypes.bool,
	isDetailVisible: PropTypes.bool,
	isNotProcessedTask: PropTypes.bool,
	isSelected: PropTypes.bool,
	isTask: PropTypes.bool,
	markSelectedAllState: PropTypes.bool,
	onDetailVisibilityChange: PropTypes.func,
	productsCodelist: PropTypes.array,
	reloadCurrentPage: PropTypes.func,
	task: PropTypes.object,
	taskSubtypesCodelist: PropTypes.array,
};

export default TaskTableBody;
